import { API, getRequest } from '@/api/api';
import { PERMISSIONS, type Permission } from '@/permissions/permissions.catalog';

import type {
  ActivitySearchResultDto,
  ChatSearchResultDto,
  DocumentSearchResultDto,
  GlobalSearchResponse,
  PeopleSearchResultDto,
  SearchSource,
  ServiceProviderSearchResultDto,
  VesselSearchResultDto,
  WalletSearchResultDto,
} from './global-search.dto';

/**
 * Shortest query the backends accept — every service validates `required` on
 * `search`, and one or two characters match half the vessel anyway.
 */
export const MIN_SEARCH_LENGTH = 2;

/**
 * Longest query we will send. The services disagree on their own ceiling
 * (people 100, vessel/activity/service-provider 200, document 255, wallet 500)
 * and each answers 400 above it, so the fan-out clamps to the strictest.
 */
export const MAX_SEARCH_LENGTH = 100;

/** Hits per category per service. 13 categories come back from vessel alone. */
export const DEFAULT_SEARCH_LIMIT = 5;

/**
 * Every service takes the same two required params and reads the caller's
 * vessel/service from the `X-Service-Id` header that api.ts injects — nothing
 * here has to pass an id. `people` also accepts `?service_id=`, but falls back
 * to the same header, so it is left off.
 */
const params = (search: string, limit: number) => ({
  search: search.slice(0, MAX_SEARCH_LENGTH),
  limit,
});

// The path differs per service because two of the axios instances are rooted at
// /api/v1 rather than at their own service (see APIS in api.ts).
const ENDPOINTS: Record<SearchSource, { api: API; url: string }> = {
  activity: { api: API.ACTIVITY, url: '/search' },
  chat: { api: API.CHAT, url: '/rooms/search' },
  documents: { api: API.DOCUMENTS, url: 'documents/search' },
  people: { api: API.PEOPLE, url: '/search' },
  'service-providers': { api: API.SERVICE_PROVIDERS, url: '/service-provider/search' },
  vessel: { api: API.VESSEL, url: '/search' },
  wallet: { api: API.EXPENSES, url: '/search' },
};

/**
 * The rights each service's `/search` opens on, mirroring the `any*` middleware guarding it.
 *
 * A service the caller holds none of these for answers 403 to every search — a plan without
 * wallet rights turned each keystroke into a rejected request, and the palette then told the
 * user results were "missing" from a service that was never theirs to search. Holding none
 * of them is not a failure, so such a source is dropped from the fan-out silently.
 *
 * `people` is open to anyone in the service and so names nothing.
 */
const SOURCE_RIGHTS: Record<SearchSource, readonly Permission[]> = {
  activity: [
    PERMISSIONS.activity.Tasks,
    PERMISSIONS.activity.ExternalTasks,
    PERMISSIONS.activity.GetEventsInfo,
    PERMISSIONS.activity.EventsActions,
    PERMISSIONS.activity.InternalGallery,
    PERMISSIONS.activity.ExternalGallery,
  ],
  chat: [
    PERMISSIONS.communication.RoomsBasic,
    PERMISSIONS.communication.RoomsExport,
    PERMISSIONS.communication.RoomsAll,
    PERMISSIONS.communication.RoomsInternal,
    PERMISSIONS.communication.RoomsTemporary,
  ],
  documents: [PERMISSIONS.document.DocumentBasic, PERMISSIONS.document.DocumentPrivate],
  people: [],
  'service-providers': [
    PERMISSIONS['service-provider'].BasicServiceProvider,
    PERMISSIONS['service-provider'].ServiceProviderInternal,
    PERMISSIONS['service-provider'].Ports,
  ],
  vessel: [
    PERMISSIONS.vessel.GetVesselInfo,
    PERMISSIONS.vessel.EditVesselInfo,
    PERMISSIONS.vessel.Equipment,
    PERMISSIONS.activity.Maintenance,
    PERMISSIONS.vessel.GetMarinaInfo,
    PERMISSIONS.vessel.MarinaBooking,
    PERMISSIONS.vessel.Recipe,
    PERMISSIONS.vessel.ChefNote,
    PERMISSIONS.menu.GetMenuInfo,
    PERMISSIONS.menu.EditMenu,
    PERMISSIONS.menu.MenuChangeRequest,
    PERMISSIONS.logbook.DailyLog,
    PERMISSIONS.logbook.DigitalNavigationLog,
  ],
  wallet: [
    PERMISSIONS.wallet.TransactionsBasic,
    PERMISSIONS.wallet.Transactions,
    PERMISSIONS.wallet.Invoices,
    PERMISSIONS.wallet.Quotes,
    PERMISSIONS.wallet.GetBudgets,
    PERMISSIONS.wallet.EditBudgets,
  ],
};

/** The services this caller's rights let them search at all. Order is stable, for query keys. */
export const searchableSources = (rights: ReadonlySet<string>): SearchSource[] =>
  (Object.keys(SOURCE_RIGHTS) as SearchSource[]).filter((source) => {
    const required = SOURCE_RIGHTS[source];
    return required.length === 0 || required.some((name) => rights.has(name));
  });

const searchOne = async <T>(
  source: SearchSource,
  search: string,
  limit: number,
): Promise<T | undefined> => {
  const { api, url } = ENDPOINTS[source];
  const { data } = await getRequest<T>(api, url, false, params(search, limit));
  // A service the proxy cannot route is answered with the app's index.html and a 200. The
  // browser blocks it (no CORS header), but CapacitorHttp skips CORS, so on native the HTML
  // reached collectHits as the result and crashed the app. Count it as this service failing.
  if (!data || typeof data !== 'object') throw new Error(`${source} search: not a result`);
  return data;
};

/**
 * One search across the services this caller can search, in parallel.
 *
 * `sources` is `searchableSources(rights)`: a service the caller holds no right for is left
 * out rather than asked and rejected, so nothing here spends a 403 per keystroke on a
 * service that was never theirs. It is also not reported as failed — it did not fail.
 *
 * `allSettled`, not `all`, for the rest: a service can still refuse a caller who holds one
 * of its rights but none for the category searched, and one expected rejection must not cost
 * the user their whole search. Real failures are named in `failed` so the UI can say what is
 * missing instead of quietly showing a short list.
 */
export const searchEverything = async (
  search: string,
  limit: number = DEFAULT_SEARCH_LIMIT,
  sources: readonly SearchSource[] = Object.keys(SOURCE_RIGHTS) as SearchSource[],
): Promise<GlobalSearchResponse> => {
  const wanted = new Set(sources);
  const failed: SearchSource[] = [];

  const ask = async <T>(source: SearchSource): Promise<T | undefined> => {
    if (!wanted.has(source)) return undefined;
    try {
      return await searchOne<T>(source, search, limit);
    } catch {
      failed.push(source);
      return undefined;
    }
  };

  const [activity, chat, documents, people, serviceProviders, vessel, wallet] = await Promise.all([
    ask<ActivitySearchResultDto>('activity'),
    ask<ChatSearchResultDto>('chat'),
    ask<DocumentSearchResultDto>('documents'),
    ask<PeopleSearchResultDto>('people'),
    ask<ServiceProviderSearchResultDto>('service-providers'),
    ask<VesselSearchResultDto>('vessel'),
    ask<WalletSearchResultDto>('wallet'),
  ]);

  return {
    activity,
    chat,
    documents,
    people,
    serviceProviders,
    vessel,
    wallet,
    failed,
  };
};
